import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import client, { formatErr } from "../lib/api";
import { Coins, Plus, Users, Clock, Flame, Trophy, Globe, Lock } from "lucide-react";

function timeLeft(deadline) {
    const ms = new Date(deadline).getTime() - Date.now();
    if (ms <= 0) return "Ended";
    const h = Math.floor(ms / 3600000);
    const m = Math.floor((ms % 3600000) / 60000);
    if (h >= 24) return `${Math.floor(h / 24)}d`;
    if (h > 0) return `${h}h ${m}m`;
    return `${m}m`;
}

const STATUS_COLOR = {
    open: "#39FF14",
    locked: "#F59E0B",
    resolved: "#a78bfa",
    cancelled: "#52525b",
};

export default function Parleys() {
    const [mine, setMine] = useState([]);
    const [publicList, setPublicList] = useState([]);
    const [tab, setTab] = useState("mine");
    const [showForm, setShowForm] = useState(false);
    const [joinCode, setJoinCode] = useState("");

    const load = async () => {
        const [m, p] = await Promise.all([client.get("/parleys/me"), client.get("/parleys/public")]);
        setMine(m.data || []);
        setPublicList(p.data || []);
    };
    useEffect(() => { load(); }, []);

    const join = async () => {
        try {
            const r = await client.post("/parleys/join", { code: joinCode.trim().toUpperCase() });
            toast.success(`Joined ${r.data.title}`);
            setJoinCode("");
            load();
        } catch (e) { toast.error(formatErr(e)); }
    };

    return (
        <div className="px-5 pt-8 pb-28" data-testid="parleys-page">
            <div className="flex items-end justify-between">
                <div>
                    <div className="label-cap text-xs text-zinc-500">Group Stake</div>
                    <h1 className="font-display font-black text-3xl uppercase tracking-tight leading-none">Parley</h1>
                </div>
                <button
                    onClick={() => setShowForm(true)}
                    data-testid="parley-new-btn"
                    className="btn-neon px-3 py-2 flex items-center gap-1.5 text-xs"
                >
                    <Plus size={14} /> New
                </button>
            </div>
            <p className="text-xs text-zinc-500 mt-3 leading-relaxed">
                Pool $NoEx with friends, agree on a target, anyone who completes splits the forfeiters' stakes. If nobody completes, the house keeps it.
            </p>

            <div className="mt-5 flex gap-2 border-b border-charcoal-500">
                {[
                    { k: "mine", l: `Mine (${mine.length})` },
                    { k: "public", l: `Open Pools (${publicList.length})` },
                ].map((t) => (
                    <button
                        key={t.k}
                        onClick={() => setTab(t.k)}
                        data-testid={`parley-tab-${t.k}`}
                        className={`label-cap text-xs px-3 py-2 -mb-px border-b-2 transition-all ${
                            tab === t.k ? "border-neon-green text-neon-green" : "border-transparent text-zinc-500 hover:text-white"
                        }`}
                    >
                        {t.l}
                    </button>
                ))}
            </div>

            <div className="mt-4 flex gap-2">
                <input
                    placeholder="Join with code (P-XXXXXX)"
                    value={joinCode}
                    onChange={(e) => setJoinCode(e.target.value)}
                    data-testid="parley-join-input"
                    className="flex-1 bg-charcoal-900 border border-charcoal-500 px-3 py-2 font-mono uppercase text-sm"
                />
                <button
                    onClick={join}
                    disabled={!joinCode}
                    data-testid="parley-join-btn"
                    className="btn-neon px-4 disabled:opacity-40"
                >
                    Join
                </button>
            </div>

            <div className="mt-5 space-y-3">
                {(tab === "mine" ? mine : publicList).length === 0 && (
                    <div className="surface-1 p-8 text-center text-zinc-500 text-sm">
                        {tab === "mine" ? "You're not in any parleys yet." : "No open pools right now."}
                    </div>
                )}
                {(tab === "mine" ? mine : publicList).map((p) => (
                    <Link key={p.id} to={`/app/parleys/${p.id}`} data-testid={`parley-card-${p.id}`}>
                        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="surface-2 p-4 hover:border-neon-green transition-colors">
                            <div className="flex items-start justify-between">
                                <div className="min-w-0 flex-1">
                                    <div className="flex items-center gap-2">
                                        {p.visibility === "public" ? <Globe size={11} className="text-neon-green" /> : <Lock size={11} className="text-zinc-500" />}
                                        <span className="font-mono text-[10px] text-zinc-500 tracking-widest">{p.code}</span>
                                        <span
                                            className="label-cap text-[9px] px-1.5 py-0.5 ml-auto"
                                            style={{
                                                background: (STATUS_COLOR[p.status] || "#52525b") + "22",
                                                color: STATUS_COLOR[p.status] || "#52525b",
                                                border: `1px solid ${STATUS_COLOR[p.status] || "#52525b"}`,
                                            }}
                                        >
                                            {p.status}
                                        </span>
                                    </div>
                                    <div className="font-display font-black uppercase text-lg leading-tight mt-1 truncate">{p.title}</div>
                                    <div className="text-xs text-zinc-400 mt-1">
                                        {p.target_type === "reps"
                                            ? `${p.target_count} reps`
                                            : p.target_type === "duration_seconds"
                                              ? `${p.target_count} sec`
                                              : `${(p.target_count / 1000).toFixed(2)} km`} · {p.target_exercise}
                                    </div>
                                </div>
                            </div>
                            <div className="mt-3 flex items-center gap-4 text-xs">
                                <span className="flex items-center gap-1 text-neon-green font-bold">
                                    <Coins size={11} /> {p.stake_amount}
                                </span>
                                <span className="flex items-center gap-1 text-zinc-500">
                                    <Users size={11} /> {(p.member_ids || []).length}/{p.max_members}
                                </span>
                                <span className="flex items-center gap-1 text-zinc-500">
                                    <Clock size={11} /> {timeLeft(p.deadline_at)}
                                </span>
                                <span className="ml-auto text-neon-orange font-bold">
                                    Pot {p.stake_amount * (p.member_ids || []).length}
                                </span>
                            </div>
                        </motion.div>
                    </Link>
                ))}
            </div>

            {showForm && <NewParleyForm onClose={() => { setShowForm(false); load(); }} />}
        </div>
    );
}

const EXERCISES_REPS = ["push-up", "pull-up", "squat", "lunge", "burpee", "crunch", "jumping-jack", "mountain-climber"];
const EXERCISES_DURATION = ["plank", "wall-sit"];
const EXERCISES_DISTANCE = ["run", "walk"];

function defaultsFor(t) {
    if (t === "reps") return { exercise: "push-up", count: 100 };
    if (t === "duration_seconds") return { exercise: "plank", count: 60 };
    return { exercise: "run", count: 5000 };  // distance_meters → 5km
}

function NewParleyForm({ onClose }) {
    const [form, setForm] = useState({
        title: "", description: "",
        stake_amount: 100,
        target_type: "reps",
        target_exercise: "push-up",
        target_count: 100,
        deadline_hours: 24,
        visibility: "private",
        min_members: 2,
        max_members: 5,
    });
    const [busy, setBusy] = useState(false);
    const inp = "w-full bg-charcoal-900 border border-charcoal-500 px-3 py-2";

    const create = async () => {
        setBusy(true);
        try {
            await client.post("/parleys", form);
            toast.success("Parley created — share the code");
            onClose();
        } catch (e) { toast.error(formatErr(e)); }
        finally { setBusy(false); }
    };

    return (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-end sm:items-center justify-center p-4" onClick={onClose}>
            <div className="surface-2 p-5 w-full max-w-md space-y-3 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="parley-form">
                <div>
                    <div className="label-cap text-[10px] text-neon-green">New Parley</div>
                    <h2 className="font-display font-black text-xl uppercase">Set The Stakes</h2>
                </div>
                <input placeholder="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className={inp} data-testid="pf-title" />
                <textarea placeholder="Description (optional)" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className={inp + " min-h-[60px]"} />

                <div className="grid grid-cols-2 gap-2">
                    <label className="text-xs">
                        <div className="text-zinc-500 mb-1">Stake ($NoEx)</div>
                        <input type="number" min={1} value={form.stake_amount} onChange={(e) => setForm({ ...form, stake_amount: Number(e.target.value) })} className={inp} data-testid="pf-stake" />
                    </label>
                    <label className="text-xs">
                        <div className="text-zinc-500 mb-1">Deadline (hours)</div>
                        <input type="number" min={1} max={720} value={form.deadline_hours} onChange={(e) => setForm({ ...form, deadline_hours: Number(e.target.value) })} className={inp} data-testid="pf-deadline" />
                    </label>
                </div>

                <div className="grid grid-cols-2 gap-2">
                    <label className="text-xs">
                        <div className="text-zinc-500 mb-1">Type</div>
                        <select value={form.target_type} onChange={(e) => {
                            const t = e.target.value;
                            const d = defaultsFor(t);
                            setForm({ ...form, target_type: t, target_exercise: d.exercise, target_count: d.count });
                        }} className={inp} data-testid="pf-type">
                            <option value="reps">Reps</option>
                            <option value="duration_seconds">Duration (seconds)</option>
                            <option value="distance_meters">Distance (meters)</option>
                        </select>
                    </label>
                    <label className="text-xs">
                        <div className="text-zinc-500 mb-1">Exercise</div>
                        <select value={form.target_exercise} onChange={(e) => setForm({ ...form, target_exercise: e.target.value })} className={inp} data-testid="pf-exercise">
                            {(form.target_type === "reps" ? EXERCISES_REPS : form.target_type === "duration_seconds" ? EXERCISES_DURATION : EXERCISES_DISTANCE).map((x) => <option key={x} value={x}>{x}</option>)}
                        </select>
                    </label>
                </div>

                <label className="text-xs block">
                    <div className="text-zinc-500 mb-1">Target {form.target_type === "reps" ? "reps" : form.target_type === "duration_seconds" ? "seconds" : "meters"}</div>
                    <input type="number" min={1} value={form.target_count} onChange={(e) => setForm({ ...form, target_count: Number(e.target.value) })} className={inp} data-testid="pf-target" />
                </label>

                <div className="grid grid-cols-2 gap-2">
                    <label className="text-xs">
                        <div className="text-zinc-500 mb-1">Min members</div>
                        <input type="number" min={2} max={50} value={form.min_members} onChange={(e) => setForm({ ...form, min_members: Number(e.target.value) })} className={inp} />
                    </label>
                    <label className="text-xs">
                        <div className="text-zinc-500 mb-1">Max members</div>
                        <input type="number" min={2} max={50} value={form.max_members} onChange={(e) => setForm({ ...form, max_members: Number(e.target.value) })} className={inp} />
                    </label>
                </div>

                <label className="text-xs">
                    <div className="text-zinc-500 mb-1">Visibility</div>
                    <select value={form.visibility} onChange={(e) => setForm({ ...form, visibility: e.target.value })} className={inp} data-testid="pf-visibility">
                        <option value="private">Private (invite-only)</option>
                        <option value="public">Public (anyone can join with code)</option>
                    </select>
                </label>

                <div className="flex gap-2 pt-2">
                    <button onClick={onClose} className="flex-1 surface-1 py-2 label-cap text-xs">Cancel</button>
                    <button onClick={create} disabled={busy || !form.title} data-testid="pf-submit" className="flex-1 btn-neon py-2 disabled:opacity-40">
                        {busy ? "Locking..." : `Stake ${form.stake_amount}`}
                    </button>
                </div>
            </div>
        </div>
    );
}
